import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { useOrder } from '../OrderContext';
import BartenderPickIcon from '../components/BartenderPickIcon';

type Option = {
  /** Emoji or image URL. For mode 'ai' the icon is the BartenderPickIcon component instead. */
  icon: string;
  title: string;
  desc: string;
  route: string;
  mode: 'main' | 'custom' | 'ai';
};

const OPTIONS: Option[] = [
  { icon: '🍹', title: 'mainMenuTitle',  desc: 'mainMenuDesc',  route: '/select',        mode: 'main'   },
  { icon: 'https://img.icons8.com/?size=96&id=ts2wH6a8QMv7&format=png', title: 'customMixTitle', desc: 'customMixDesc', route: '/custom-mix',    mode: 'custom' },
  { icon: '', title: 'aiPickTitle',    desc: 'aiPickDesc',    route: '/ai-pick-menu',  mode: 'ai'     },
];

const MenuTypePage = () => {
  const navigate = useNavigate();
  const { resetOrder, updateOrder, order } = useOrder();
  const [pressed, setPressed] = useState<string | null>(null);

  const handlePick = (option: Option) => {
    const user = order.user;
    resetOrder();
    updateOrder({ orderMode: option.mode, user });
    navigate(option.route);
  };

  return (
    <div className="kiosk-app rush-tech-bg relative flex h-full w-full flex-col overflow-hidden [animation:pageFadeIn_0.35s_ease_both]">
      <div className="noise-overlay" />

      {/* Cards — 3 equal rows filling remaining height */}
      <div className="relative z-10 flex flex-1 flex-col gap-3 px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
        {OPTIONS.map((opt, i) => {
          const isPressed = pressed === opt.mode;
          return (
            <button
              key={opt.mode}
              type="button"
              className={`touch-target relative flex flex-1 w-full cursor-pointer flex-row items-center gap-4 overflow-hidden rounded-[18px] border px-5 text-left transition-all duration-150 [animation:pageFadeIn_0.4s_ease_both] sm:gap-10 sm:px-8 ${
                isPressed
                  ? 'scale-[0.97] border-[var(--gold-lt)] bg-[var(--accent-pressed-bg)] shadow-[0_0_40px_var(--accent-pressed-shadow)]'
                  : 'border-[var(--border)] bg-[var(--card)] shadow-[var(--card-shadow)]'
              }`}
              style={{ animationDelay: `${i * 0.08}s` }}
              onPointerDown={() => setPressed(opt.mode)}
              onPointerUp ={() => { setPressed(null); handlePick(opt); }}
              onPointerCancel={() => setPressed(null)}
              onPointerLeave={() => setPressed(null)}
            >
              {/* left accent line */}
              <div className={`absolute left-0 top-0 h-full w-1 rounded-l-[18px] transition-all duration-150 ${isPressed ? 'bg-[var(--gold-lt)]' : 'bg-[var(--accent-bar)]'}`} />

              {/* Icon */}
              <div className="flex h-[clamp(3rem,6vw,5rem)] w-[clamp(3rem,6vw,5rem)] shrink-0 items-center justify-center [filter:drop-shadow(0_2px_14px_var(--accent-glow))]">
                {opt.mode === 'ai'
                  ? <BartenderPickIcon className="block h-[clamp(3.2rem,6.4vw,5.4rem)] w-[clamp(3.2rem,6.4vw,5.4rem)]" />
                  : opt.icon.startsWith('http')
                  ? <img src={opt.icon} alt="" className="block h-[clamp(3rem,6vw,5rem)] w-[clamp(3rem,6vw,5rem)] object-contain" />
                  : <span className="block leading-none text-center text-[clamp(3rem,6vw,5rem)]">{opt.icon}</span>}
              </div>

              {/* Text */}
              <div className="flex flex-col gap-1.5">
                <div className="font-display font-bold leading-tight text-[var(--cream)]" style={{ fontSize: 'clamp(1.6rem, 3.2vw, 2.6rem)' }}>
                  {t[opt.title as keyof typeof t] as string}
                </div>
                <div className="text-[var(--muted)]" style={{ fontFamily: "'Sora', sans-serif", fontSize: 'clamp(0.95rem, 1.8vw, 1.3rem)', lineHeight: 1.4 }}>
                  {t[opt.desc as keyof typeof t] as string}
                </div>
              </div>

              {/* Right arrow */}
              <div className="ml-auto w-8 shrink-0 text-center text-[var(--gold-lt)] opacity-50" style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)' }}>›</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MenuTypePage;
